import { useCallback, useEffect, useRef } from "react";
import type { JSX } from "react";

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly message: string;
  /** Optional secondary detail text (e.g. disk size warning). */
  readonly detail?: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  /** Use "danger" for destructive actions (red confirm button). */
  readonly variant?: "default" | "danger";
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  detail,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handler);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { window.removeEventListener("keydown", handler); };
  }, [open, onCancel]);

  // Close on backdrop click
  const handleBackdrop = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onCancel();
    },
    [onCancel],
  );

  if (!open) return null;

  const confirmClasses =
    variant === "danger"
      ? "bg-red-500 hover:bg-red-400 text-white"
      : "bg-accent hover:bg-accent/90 text-white";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-[2px]"
      onClick={handleBackdrop}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-[420px] rounded-xl bg-surface border border-edge shadow-2xl mx-4"
      >
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-[15px] font-semibold text-content">{title}</h3>
          <p className="text-[13px] text-content-secondary mt-2 leading-relaxed">{message}</p>
          {detail && (
            <p className="text-[12px] text-content-muted mt-2 leading-relaxed">{detail}</p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 pb-4 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-[13px] font-medium text-content-secondary
              hover:text-content hover:bg-surface-hover transition-colors cursor-pointer
              border-none bg-transparent"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-colors cursor-pointer
              border-none ${confirmClasses}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
